import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Activity, ArrowRight, Loader2 } from "lucide-react";

/**
 * Agent activity strip — iter 31.29.
 *
 * Surfaces the last 24h of agent activity at the top of the Brief.
 * Source: GET /api/agents/activity/24h.
 *
 * Self-reporting: Two reads this section without No. 6 needing to
 * summarise. The headline is "Mureka · 4 played · Barclaycard · 2
 * transactions · ..." — the operation reporting on itself.
 */
export default function AgentActivityStrip() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        api.get("/agents/activity/24h")
            .then((r) => setData(r.data))
            .catch(() => setData(null))
            .finally(() => setLoading(false));
    }, []);

    if (loading) return null;
    if (!data || data.total_entries === 0) return null;

    return (
        <section
            className="paper-card border border-stamp-red/30 p-4 mb-6"
            data-testid="agent-activity-strip"
        >
            <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
                <div className="flex items-center gap-2">
                    <Activity className="w-4 h-4 text-stamp-red" />
                    <div className="font-heading uppercase tracking-[0.2em] text-sm text-ink">
                        Agents · last 24h
                    </div>
                    <div className="label-typed text-[9px] text-ink-faint ml-2">
                        {data.tagged_entries} / {data.total_entries} entries routed
                    </div>
                </div>
                <Link
                    to="/team"
                    data-testid="agent-activity-team-link"
                    className="text-[10px] font-heading uppercase tracking-[0.22em] text-stamp-red hover:text-ink inline-flex items-center gap-1"
                >
                    Team <ArrowRight className="w-3 h-3" />
                </Link>
            </div>

            <p
                className="font-body text-sm text-ink leading-relaxed mb-3"
                data-testid="agent-activity-headline"
            >
                {data.headline}
            </p>

            {data.by_agent?.length > 0 && (
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-2">
                    {data.by_agent.slice(0, 10).map((b) => (
                        <div
                            key={b.agent_key}
                            className="paper-card border border-ink/15 p-2"
                            data-testid={`agent-activity-${b.agent_key}`}
                        >
                            <div className="flex items-baseline justify-between gap-2">
                                <span className="font-heading text-xs uppercase tracking-[0.04em] text-ink truncate">
                                    {b.agent_name}
                                </span>
                                <span className="label-typed text-[9px] text-ink-faint">
                                    #{b.number_display || b.number || "?"}
                                </span>
                            </div>
                            <div className="font-heading text-2xl text-stamp-red mt-1">
                                {b.total}
                            </div>
                            <div className="label-typed text-[9px] text-ink-soft truncate">
                                {Object.keys(b.kinds || {})[0]?.replace(/_/g, " ") || "filings"}
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </section>
    );
}
